import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class CookieService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  // Domínio raiz compartilhado — cookies aqui ficam visíveis para TODOS os subdomínios.
  // Limitação conhecida (SEC): cookies definidos via document.cookie NÃO podem ser HttpOnly.
  // Mitigação completa (refresh HttpOnly + access em memória) exige mudança coordenada na API.
  // Até lá, o blast radius de XSS cobre todo o ecossistema sob este domínio.
  private readonly sharedDomain = '.unimedinconfidentes.coop.br';

  // Fallback caso o token não seja um JWT válido com campo exp (7 dias)
  private readonly fallbackMaxAge = 60 * 60 * 24 * 7;

  /**
   * Extrai o tempo de expiração diretamente do payload JWT.
   * Retorna o Max-Age em segundos a partir de agora,
   * ou o fallback se o token não tiver campo exp ou não for um JWT.
   */
  private getMaxAgeFromToken(token: string): number {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return this.fallbackMaxAge;

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const payload = JSON.parse(jsonPayload);
      if (payload?.exp) {
        const secondsRemaining = payload.exp - Math.floor(Date.now() / 1000);
        return secondsRemaining > 0 ? secondsRemaining : this.fallbackMaxAge;
      }
    } catch {
      // Token não é JWT ou está malformado
    }
    return this.fallbackMaxAge;
  }

  /**
   * Grava o cookie SEMPRE com o domínio compartilhado.
   * O SGI é a fonte da verdade: todo token gravado aqui já nasce
   * disponível para logistica., financeiro., etc.
   * O Max-Age é extraído automaticamente do exp do JWT.
   */
  set(key: string, value: string): void {
    if (!this.isBrowser) return;

    const maxAgeSeconds = this.getMaxAgeFromToken(value);
    const hostname = window.location.hostname;
    const isProductionDomain =
      hostname.endsWith(this.sharedDomain) ||
      hostname === this.sharedDomain.substring(1);

    let cookieString = `${key}=${encodeURIComponent(value)}; path=/; SameSite=Lax; Max-Age=${maxAgeSeconds}`;

    if (isProductionDomain) {
      // Força o domínio compartilhado para que as filhas enxerguem o cookie
      cookieString += `; domain=${this.sharedDomain}; Secure`;
    } else if (window.location.protocol === 'https:') {
      // Ambiente local em HTTPS (ex: localhost com certificado)
      cookieString += '; Secure';
    }

    document.cookie = cookieString;
  }

  get(key: string): string | null {
    if (!this.isBrowser) return null;

    const nameEQ = `${key}=`;
    for (const raw of document.cookie.split(';')) {
      const c = raw.trim();
      if (c.startsWith(nameEQ)) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }
    return null;
  }

  /**
   * Remove o cookie dos dois escopos (com e sem domínio compartilhado)
   * para garantir limpeza completa no logout.
   */
  remove(key: string): void {
    if (!this.isBrowser) return;

    const baseCookie = `${key}=; path=/; Max-Age=0; SameSite=Lax; Secure`;

    // Remove do domínio compartilhado (visível pelas filhas)
    document.cookie = `${baseCookie}; domain=${this.sharedDomain}`;
    // Remove do escopo local também, por segurança
    document.cookie = baseCookie;
  }

  clear(prefix: string): void {
    if (!this.isBrowser) return;

    for (const cookie of document.cookie.split(';')) {
      const eqPos = cookie.indexOf('=');
      const name = (eqPos > -1 ? cookie.substring(0, eqPos) : cookie).trim();
      if (name.startsWith(prefix)) {
        this.remove(name);
      }
    }
  }

  // Utilitário de debug — use no console do browser para inspecionar cookies visíveis
  debug(): Record<string, string> {
    if (!this.isBrowser) return {};

    return Object.fromEntries(
      document.cookie.split(';')
        .map(c => c.trim().split('='))
        .filter(([key]) => !!key)
        .map(([key, ...rest]) => [key, decodeURIComponent(rest.join('='))])
    );
  }
}